import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ApiClient } from '../../api'
import { Icon } from '../Icon'
import type { LiveWalkthrough } from '../../types'

// Walkthrough card (Live View PR L3 / spec 33 §5.4). Own .day-card in
// Manager Overview, top-right of the grid. Renders one of:
//   - Read-only sentence for non-managers: "Walkthrough completed at
//     HH:MM by <name>" OR "Walkthrough not yet recorded."
//   - Manager toggle: checkbox affordance + sentence + confirm modal
//     on every state flip. PATCH /api/events/{id}/walkthrough.
//
// Toast strings (raised by the parent LiveDayView via showToast):
//   - On set:   "Walkthrough recorded."
//   - On clear: "Walkthrough cleared."
//
// Confirm modal copy is exact per spec 33 §5.4:
//   - Set:   "Mark walkthrough complete now? This is recorded against
//             your name."
//   - Clear: "Clear walkthrough record? Build Progress will revert."

interface Props {
  eventId: number
  walkthrough: LiveWalkthrough
  canEdit: boolean
  showToast: (msg: string) => void
}

export function WalkthroughCard({ eventId, walkthrough, canEdit, showToast }: Props) {
  const [confirming, setConfirming] = useState<'set' | 'clear' | null>(null)
  const qc = useQueryClient()

  const isComplete = walkthrough.completedAt != null

  const mut = useMutation({
    mutationFn: (completed: boolean) => ApiClient.setEventWalkthrough(eventId, completed),
    onSuccess: (_data, completed) => {
      qc.invalidateQueries({ queryKey: ['live-pulse', eventId] })
      qc.invalidateQueries({ queryKey: ['event', eventId] })
      showToast(completed ? 'Walkthrough recorded.' : 'Walkthrough cleared.')
      setConfirming(null)
    }
  })

  const completedAtText = walkthrough.completedAt
    ? new Date(walkthrough.completedAt).toLocaleTimeString('en-GB', {
        hour: '2-digit', minute: '2-digit'
      })
    : null

  return (
    <>
      <div className="day-card">
        <div className="day-card-head flex items-center justify-between" style={{ display: 'flex' }}>
          <span>Walkthrough</span>
          {isComplete && (
            <span
              className="inline-flex items-center gap-1.5 text-xs font-semibold"
              style={{ color: '#80e8a8', letterSpacing: '0.04em' }}
            >
              <Icon name="check-circle" size={13} />
              Complete
            </span>
          )}
        </div>

        <div className="flex items-start gap-3">
          {canEdit ? (
            <button
              type="button"
              onClick={() => setConfirming(isComplete ? 'clear' : 'set')}
              disabled={mut.isPending}
              aria-pressed={isComplete}
              aria-label="Toggle manager walkthrough"
              className="flex-shrink-0 transition-colors disabled:opacity-50"
              style={{
                width: 24,
                height: 24,
                borderRadius: 6,
                background: isComplete ? '#80e8a8' : 'rgba(245, 241, 234, 0.06)',
                border: `1px solid ${isComplete ? '#80e8a8' : 'rgba(245, 241, 234, 0.25)'}`,
                color: '#0a4a3a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: 14,
                cursor: mut.isPending ? 'wait' : 'pointer'
              }}
            >
              {isComplete ? '✓' : ''}
            </button>
          ) : (
            <span
              aria-hidden
              style={{
                width: 24,
                height: 24,
                borderRadius: 6,
                background: isComplete ? '#80e8a8' : 'rgba(245, 241, 234, 0.06)',
                border: `1px solid ${isComplete ? '#80e8a8' : 'rgba(245, 241, 234, 0.18)'}`,
                color: '#0a4a3a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: 14,
                flexShrink: 0
              }}
            >
              {isComplete ? '✓' : ''}
            </span>
          )}

          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm" style={{ color: '#fdfbf6' }}>
              Manager walkthrough
            </div>
            <div className="text-xs mt-1" style={{ color: 'rgba(245, 241, 234, 0.7)' }}>
              {isComplete
                ? <>Completed at <span className="font-mono">{completedAtText}</span> by {walkthrough.completedByName ?? 'a manager'}</>
                : 'Not yet recorded.'}
            </div>
            {canEdit && (
              <div className="text-[11px] mt-2" style={{ color: 'rgba(245, 241, 234, 0.5)' }}>
                {isComplete ? 'Re-tick to re-record. Untick to clear.' : 'Tick once you have walked the site and checked stations.'}
              </div>
            )}
            {mut.isError && (
              <div className="text-xs mt-2" style={{ color: '#ef9a9a' }}>
                Could not update walkthrough: {(mut.error as Error).message}
              </div>
            )}
          </div>
        </div>
      </div>

      {confirming && (
        <WalkthroughConfirmModal
          kind={confirming}
          pending={mut.isPending}
          onCancel={() => setConfirming(null)}
          onConfirm={() => mut.mutate(confirming === 'set')}
        />
      )}
    </>
  )
}

function WalkthroughConfirmModal({
  kind,
  pending,
  onCancel,
  onConfirm
}: {
  kind: 'set' | 'clear'
  pending: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  const body = kind === 'set'
    ? 'Mark walkthrough complete now? This is recorded against your name.'
    : 'Clear walkthrough record? Build Progress will revert.'
  const cta = kind === 'set' ? 'Mark complete' : 'Clear record'

  return (
    <div
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[420] flex items-center justify-center"
      style={{ background: 'rgba(0, 0, 0, 0.55)', padding: 20 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-parchment text-ink rounded-xl w-full"
        style={{ maxWidth: 420, padding: 24, boxShadow: '0 24px 60px -10px rgba(0,0,0,0.45)' }}
      >
        <h3 className="font-display text-lg mb-2">
          {kind === 'set' ? 'Mark walkthrough complete' : 'Clear walkthrough'}
        </h3>
        <p className="text-sm mb-5">{body}</p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            autoFocus
            className="px-4 py-2 rounded text-sm text-ink-soft hover:bg-white disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={pending}
            className={
              'px-4 py-2 rounded text-sm font-semibold disabled:opacity-50 ' +
              (kind === 'set'
                ? 'bg-green text-parchment hover:bg-green/90'
                : 'bg-oxblood text-parchment hover:bg-oxblood/90')
            }
          >
            {pending ? 'Saving…' : cta}
          </button>
        </div>
      </div>
    </div>
  )
}
